
























































import { computed, defineComponent, ref, watch, inject } from "@vue/composition-api";
import { MessageLinkSubject } from "@scrile/api-provider/dist/api/MessageLinksProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { blockPurchaseIfAgeNotVerified } from "@/lib/ageVerificationPurchaseGuard";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { getCurrencyCode, getCurrencySymbol } from "@/lib/moneyHelpers";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInput from "@/components/BaseInput";
import BaseSendMessage from "@/components/BaseSendMessage";
import BaseSpinner from "@/components/BaseSpinner";
import PaidActionError from "@/components/PaidActionError";

const NOT_ENOUGH_FUNDS_ERROR = "{com.scrile.money.validation.SpendingBalanceValidator.message}";

export default defineComponent({
  name: "TipController",
  components: { BaseSpinner, BaseSendMessage, BaseInput, BaseIcon, PaidActionError, BaseIconButton },
  props: {
    threadId: { type: String },
    transparent: { type: Boolean, default: false },
    linkSubject: { type: Object as () => MessageLinkSubject },
  },
  setup(props, { emit }) {
    const data = inject<{ user: UserPublicData | null }>("messageData", { user: null });

    const { isMember, authUser } = useAuthUser();

    const tipValue = ref("");
    const processing = ref(false);
    const currencySymbol = $t(getCurrencySymbol(getCurrencyCode()));
    const show = ref(false);
    const showPaidError = ref(false);

    const userId = computed(() => data.user?.id ?? "");
    const activateTip = computed(() => isMember.value && checkRole(data.user?.role || "", "creator"));

    const openerIcon = computed(() => (show.value ? "sci-close" : "sci-gift"));

    const regexInput = currencySymbol
      .split("")
      .map(i => {
        if (["[", "]", "\\", "^", "$", ".", "|", "?", "*", "+", "(", ")"].includes(i)) {
          return `\\${i}`;
        }
        return i;
      })
      .join("")
      .concat("(\\d*)");
    const allowSend = computed(() => {
      return tipValue.value.length !== 0 && +tipValue.value > 0;
    });
    const toggleShow = () => (show.value = !show.value);
    watch(show, () => {
      tipValue.value = "";
    });

    const tipValueInput = (v: string) => {
      const value = Number(v.replace(currencySymbol, "") ?? 0) || 0;
      if (isNaN(value) || value === 0) {
        tipValue.value = "";
      } else {
        tipValue.value = value.toString();
      }
    };

    const submit = async () => {
      if (!userId.value || !allowSend.value) return;
      if (blockPurchaseIfAgeNotVerified(authUser.value)) {
        return;
      }
      try {
        processing.value = true;
        const tipAmount = tipValue.value;
        providers.TipProvider.setReceiveErrors(false);
        await providers.TipProvider.create({
          data: {
            recipientId: userId.value,
            amount: +tipAmount,
            threadId: props.threadId,
            linkSubject: props.linkSubject,
          },
        });
        emit("success");
        tipValue.value = "";
        show.value = false;
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === NOT_ENOUGH_FUNDS_ERROR,
            onSuccessCB: () => (showPaidError.value = true),
          },
        ]);
      } finally {
        processing.value = false;
        tipValue.value = "";
      }
    };

    return {
      currencySymbol,
      tipValue,
      tipValueInput,
      allowSend,
      processing,
      submit,
      activateTip,
      openerIcon,
      show,
      toggleShow,
      showPaidError,
      regexInput,
    };
  },
});
